import map from 'lodash-es/map'
import React, { Fragment, useState } from 'react'
import { SortableContainer, SortableElement } from 'react-sortable-hoc'
import { Loading } from '../../Loading'
import { DRAG_HELPER_CLASS } from '../consts'
import { TableCell } from '../TableCell/TableCell'
import { TableRow } from '../TableRow/TableRow'
import * as S from './styled'
import { TableBodyWrapper } from './TableBodyWrapper'
import type { RowGroupProps, RowProps, TableBodyProps } from './types'

const Row = SortableElement<RowProps>(
  ({
    row,
    rowIndex,
    headers,
    actions,
    sortable,
    isDragging,
    highlighted,
    compact,
    rowComponent,
    href,
    target,
    renderFooter,
    getRowLink,
    onClick,
  }: RowProps) => {
    const renderCells = () => {
      return map(headers, (header) => (
        <TableCell
          key={header.id}
          width={header.width}
          $minWidth={header.minWidth}
          height={header.height}
          $minHeight={header.minHeight}
          $alignment={header.alignment}
          $alignItems={header.alignItems}
          $justifyContent={header.justifyContent}
          $compact={compact}
        >
          {header.render(row, rowIndex)}
        </TableCell>
      ))
    }

    return (
      <Fragment key={row.id}>
        <TableRow
          component={rowComponent}
          row={row}
          to={getRowLink && getRowLink(row)}
          href={href}
          target={target}
          sortable={sortable}
          isDragging={isDragging}
          actions={actions}
          highlighted={highlighted}
          renderFooter={renderFooter}
          onClick={onClick}
        >
          {renderCells()}
        </TableRow>
      </Fragment>
    )
  },
)

const RowGroup = SortableContainer<RowGroupProps>(
  ({
    data,
    headers,
    actions,
    sortable,
    compact,
    isDragging,
    rowComponent,
    rowTarget,
    renderFooter,
    getRowLink,
    getRowHref,
    getRowHighlighted,
    onRowClick,
  }: RowGroupProps) => (
    <S.RowGroup role="rowgroup" $isDragging={!!isDragging}>
      {map(data, (row, index) => (
        <Row
          key={row.id}
          row={row}
          rowIndex={index}
          index={index}
          headers={headers}
          actions={actions}
          sortable={sortable}
          isDragging={isDragging}
          compact={compact}
          rowComponent={rowComponent}
          href={getRowHref ? getRowHref(row) : undefined}
          target={rowTarget}
          disabled={!sortable}
          highlighted={getRowHighlighted ? getRowHighlighted(row) : false}
          renderFooter={renderFooter}
          getRowLink={getRowLink}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
        />
      ))}
    </S.RowGroup>
  ),
)

export const TableBody = ({
  data,
  headers,
  actions,
  sortable,
  compact,
  maxHeight,
  rowComponent,
  rowTarget,
  isLoading,
  renderFooter,
  getRowLink,
  getRowHref,
  getRowHighlighted,
  onRowClick,
  onPositionChange,
}: TableBodyProps) => {
  const [isDragging, setIsDragging] = useState(false)

  const onSortStart = () => {
    document.body.style.cursor = 'grabbing'
    setIsDragging(true)
  }

  const handleSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    document.body.style.cursor = ''
    setIsDragging(false)

    if (oldIndex !== newIndex && onPositionChange) {
      onPositionChange(oldIndex, newIndex)
    }
  }

  return (
    <TableBodyWrapper maxHeight={maxHeight}>
      <Loading isLoading={isLoading}>
        <RowGroup
          data={data}
          headers={headers}
          actions={actions}
          sortable={sortable}
          compact={compact}
          isDragging={isDragging}
          rowComponent={rowComponent}
          rowTarget={rowTarget}
          getRowLink={getRowLink}
          getRowHref={getRowHref}
          getRowHighlighted={getRowHighlighted}
          helperClass={DRAG_HELPER_CLASS}
          axis="y"
          useDragHandle
          renderFooter={renderFooter}
          onRowClick={onRowClick}
          onSortStart={onSortStart}
          onSortEnd={handleSortEnd}
        />
      </Loading>
    </TableBodyWrapper>
  )
}
